import { useNavigate } from "react-router-dom";
import { useForm, SubmitHandler } from "react-hook-form";
import useAuthStore from "../../store/authStore";
import AuthPageLayout from "../../components/authPageLayout/AuthPageLayout";
import { useState } from "react";
import Avatar from "../../components/Avatar";

type FormValues = {
  title: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  profilePicture: FileList;
};

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const Register = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
    clearErrors,
    setError,
  } = useForm<FormValues>();
  const [filePreview, setFilePreview] = useState<any>(null);
  const [file, setFile] = useState<File | null>(null);

  const { registerUser, isBusyAuthenticating, message } = useAuthStore();
  const navigate = useNavigate();

  const onRegister: SubmitHandler<FormValues> = async (data) => {
    let profilePicture = file;
    if (!profilePicture) {
      setError("profilePicture", {
        type: "manual",
        message: "Please upload a valid file.",
      });
      return;
    }
    const registerVals = { ...data, profilePicture };
    const res = await registerUser(registerVals);
    if (res.code === 1001) {
      navigate("/login");
    }
  };

  const onClear = () => {
    reset();
  };

  const handleFileChange = (event: any) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      // Validate file size
      if (selectedFile.size > MAX_FILE_SIZE) {
        setError("profilePicture", {
          type: "manual",
          message: "File size exceeds 10MB. Please choose a smaller file.",
        });
        setFilePreview(null); // Reset preview
      } else {
        clearErrors("profilePicture"); // Clear the error if the file is valid
        setFile(selectedFile); // Set the file
        const fileURL = URL.createObjectURL(selectedFile);
        setFilePreview(fileURL); // Set the preview URL
      }
    }
  };
  return (
    <AuthPageLayout isBusy={isBusyAuthenticating}>
      <div className="min-h-screen bg-cover bg-center">
        <div className="flex flex-col items-center justify-center min-h-screen px-0 sm:px-6 lg:px-8">
          <div className="text-center my-8 sm:my-12 px-4">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-primary-Default dark:text-secondary-Default">
              Register for an account
            </h1>
            <p className="text-gray-600 dark:text-gray-200 mt-4 text-sm sm:text-base">
              Join the league and swing into action - your golf journey starts
              here!
            </p>
          </div>

          <div className="w-full max-w-lg sm:max-w-2xl bg-secondary-Default bg-opacity-70 dark:bg-primary-Default dark:bg-opacity-70 rounded-lg shadow-lg backdrop-blur-md py-10 px-6 mb-[400px]">
            <form className="space-y-12" onSubmit={handleSubmit(onRegister)}>
              <div className="sm:col-span-6 flex flex-col items-center justify-center">
                <label
                  htmlFor="profilePicture"
                  className="block text-sm font-medium leading-6 text-gray-500 dark:text-gray-400"
                >
                  Profile Picture
                </label>
                <div className="flex gap-5 justify-center items-center">
                  <input
                    style={{ height: "39px", width: "109px" }}
                    id="profilePicture"
                    {...register("profilePicture", {
                      required:
                        "Profile picture is required, you can change it later.",
                    })}
                    type="file"
                    onChange={handleFileChange}
                    accept="image/*"
                    className="block w-full text-sm text-gray-900 dark:text-gray-100 dark:bg-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-600 file:text-white hover:file:bg-indigo-500"
                  />
                  <Avatar
                    firstName="NC"
                    lastName="C"
                    imageUrl={filePreview}
                    className="h-20 w-20 rounded-full "
                  />
                </div>
                {errors.profilePicture && (
                  <span className="text-red-500 text-sm">
                    {errors.profilePicture.message}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                <div className="sm:col-span-2">
                  <label
                    htmlFor="title"
                    className="block text-sm font-medium leading-6 text-gray-500 dark:text-gray-400"
                  >
                    Title
                  </label>
                  <div className="mt-2">
                    <select
                      id="title"
                      {...register("title", { required: true })}
                      className="block w-full rounded-md border-0 py-1.5 px-2 bg-white text-gray-900 dark:text-gray-100 dark:bg-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-700 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    >
                      <option></option>
                      <option>Mr.</option>
                      <option>Ms.</option>
                      <option>Mrs.</option>
                      <option>Dr.</option>
                    </select>
                    {errors.title && (
                      <span className="text-red-500 text-sm">
                        Title is required
                      </span>
                    )}
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <label
                    htmlFor="firstName"
                    className="block text-sm font-medium leading-6 text-gray-500 dark:text-gray-400"
                  >
                    First name
                  </label>
                  <div className="mt-2">
                    <input
                      id="firstName"
                      {...register("firstName", { required: true })}
                      type="text"
                      className="block w-full rounded-md border-0 py-1.5 px-2 bg-white text-gray-900 dark:text-gray-100 dark:bg-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-700 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                    {errors.firstName && (
                      <span className="text-red-500 text-sm">
                        First name is required
                      </span>
                    )}
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <label
                    htmlFor="lastName"
                    className="block text-sm font-medium leading-6 text-gray-500 dark:text-gray-400"
                  >
                    Last name
                  </label>
                  <div className="mt-2">
                    <input
                      id="lastName"
                      {...register("lastName", { required: true })}
                      type="text"
                      className="block w-full rounded-md border-0 py-1.5 px-2 bg-white text-gray-900 dark:text-gray-100 dark:bg-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-700 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                    {errors.lastName && (
                      <span className="text-red-500 text-sm">
                        Last name is required
                      </span>
                    )}
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium leading-6 text-gray-500 dark:text-gray-400"
                  >
                    Email address
                  </label>
                  <div className="mt-2">
                    <input
                      id="email"
                      {...register("email", { required: true })}
                      type="email"
                      className="block w-full rounded-md border-0 py-1.5 px-2 bg-white text-gray-900 dark:text-gray-100 dark:bg-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-700 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                    {errors.email && (
                      <span className="text-red-500 text-sm">
                        Email is required
                      </span>
                    )}
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label
                    htmlFor="phoneNumber"
                    className="block text-sm font-medium leading-6 text-gray-500 dark:text-gray-400"
                  >
                    Phone Number
                  </label>
                  <div className="mt-2">
                    <input
                      id="phoneNumber"
                      {...register("phone", { required: true })}
                      type="text"
                      className="block w-full rounded-md border-0 py-1.5 px-2 bg-white text-gray-900 dark:text-gray-100 dark:bg-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-700 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                    {errors.phone && (
                      <span className="text-red-500 text-sm">
                        Phone number is required
                      </span>
                    )}
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium leading-6 text-gray-500 dark:text-gray-400"
                  >
                    Password
                  </label>
                  <div className="mt-2">
                    <input
                      id="password"
                      {...register("password", { required: true })}
                      type="password"
                      className="block w-full rounded-md border-0 py-1.5 px-2 bg-white text-gray-900 dark:text-gray-100 dark:bg-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-700 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                    {errors.password && (
                      <span className="text-red-500 text-sm">
                        Password is required
                      </span>
                    )}
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label
                    htmlFor="confirmPassword"
                    className="block text-sm font-medium leading-6 text-gray-500 dark:text-gray-400"
                  >
                    Confirm Password
                  </label>
                  <div className="mt-2">
                    <input
                      id="confirmPassword"
                      {...register("confirmPassword", {
                        required: true,
                        validate: (value) =>
                          value === getValues().password ||
                          "Passwords do not match",
                      })}
                      type="password"
                      className="block w-full rounded-md border-0 py-1.5 px-2 bg-white text-gray-900 dark:text-gray-100 dark:bg-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-700 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                    {errors.confirmPassword && (
                      <span className="text-red-500 text-sm">
                        {errors.confirmPassword.message}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-end gap-x-6">
                <p>
                  {message && (
                    <span className="text-red-500 text-sm">{message}</span>
                  )}
                </p>
                <button
                  type="button"
                  className="text-sm font-semibold leading-6 text-gray-900 dark:text-gray-100"
                  onClick={onClear}
                >
                  Clear
                </button>
                <button
                  type="submit"
                  className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:focus-visible:outline-indigo-500"
                >
                  Register
                </button>
              </div>

              <div>
                <p className="text-center text-sm text-gray-500 dark:text-gray-200">
                  Already a member?{" "}
                  <span
                    onClick={() => navigate("/login")}
                    className="font-semibold leading-6 text-indigo-600 hover:text-indigo-500 cursor-pointer"
                  >
                    Sign in
                  </span>
                </p>
                <p className="text-center text-sm text-gray-500 dark:text-gray-200">
                  <span
                    onClick={() => navigate("/")}
                    className="font-semibold leading-6 text-indigo-600 hover:text-indigo-500 cursor-pointer"
                  >
                    Cancel
                  </span>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </AuthPageLayout>
  );
};

export default Register;
